'use client'
import React from 'react'
import styled from 'styled-components'
import { useConfig } from '@/contexts/ConfigContext'
import { useLanguage } from '@/contexts/LanguageContext'
import ModuleContainer from './ModuleContainer'
import KeyboardIcon from '@mui/icons-material/Keyboard'
import KeySelector from '@/components/CustomInput/KeySelector'
import { useUI } from '@/contexts/UIContext'
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun'
import SecurityIcon from '@mui/icons-material/Security'
import ForumIcon from '@mui/icons-material/Forum'
import AppSettingsAltIcon from '@mui/icons-material/AppSettingsAlt'
import AccessibilityIcon from '@mui/icons-material/Accessibility'
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'
import HistoryIcon from '@mui/icons-material/History'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'

import PublicIcon from '@mui/icons-material/Public'
import GroupIcon from '@mui/icons-material/Group'
import Image from 'next/image'
import { getItemIcon } from '@/constants/itemIcons'

const Binds_Controls = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    transition: opacity 0.2s ease;
    width: 100%;
`

const Binds_ModuleBody = styled.div`
    display: flex;
    flex-direction: column;
    //background: rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    overflow: hidden;
    width: 100%;
    //border: 1px solid rgba(255, 255, 255, 0.05);

`

const SectionRow = styled.div`
    display: flex;
    gap: 20px;
    width: 100%;
    margin: 15px 0;
    align-items: flex-start;

    &:last-child {
        margin-bottom: 0;
    }
`

const SectionIconBinder = styled.div`
    display: none;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    min-width: 50px;
    border-radius: 12px;
    background: ${({ $bg }) => $bg || 'rgba(255,255,255,0.05)'};
    color: ${({ $color }) => $color || '#fff'};
    box-shadow: 0 0 15px ${({ $color }) => $color ? `${$color}22` : 'rgba(0,0,0,0)'};
    border: 1px solid ${({ $color }) => $color ? `${$color}44` : 'transparent'};
    margin-top: 15px; /* Visual alignment with the bar title */

    svg {
        font-size: 28px;
        filter: drop-shadow(0 0 5px ${({ $color }) => $color || 'rgba(255,255,255,0.5)'});
    }
`

const SectionContent = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
`


const Binds_LabelText = styled.span`
    font-size: 11px;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.5);
    text-transform: uppercase;
    letter-spacing: 0.8px;
    display: flex;
    align-items: center;
    gap: 6px;
`


const SlotLabel = styled.span`
    font-size: 0.65rem;
    color: rgba(255, 255, 255, 0.75);
    font-weight: 700;
    text-transform: uppercase;
    text-align: center;
`

const CommandLabel = styled.span`
    font-size: 0.5rem;
    color: rgba(255, 255, 255, 0.4);
    margin-top: -6px;
    margin-bottom: 4px;
    line-height: 1.2;
    text-transform: uppercase;
    max-width: 90%;
    text-align: center;
`

const KeyboardGridWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    align-items: flex-start;
    padding: 10px 0;
`

const KeyboardRow = styled.div`
    display: flex;
    gap: 8px;
    margin-left: ${props => props.$offset || '0px'};
`

const KeyBlock = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: ${props => props.$highlight ? 'rgba(59, 130, 246, 0.15)' : 'rgba(255, 255, 255, 0.03)'};
    border: 1px solid ${props => props.$highlight ? 'rgba(59, 130, 246, 0.4)' : 'rgba(255, 255, 255, 0.1)'};
    border-radius: 8px;
    padding: 10px;
    min-width: 85px;
    min-height: 80px;
    transition: all 0.2s;
    
    &:hover {
         border-color: ${props => props.$highlight ? 'rgba(59, 130, 246, 0.8)' : 'rgba(255, 255, 255, 0.3)'};
         background: ${props => props.$highlight ? 'rgba(59, 130, 246, 0.25)' : 'rgba(255, 255, 255, 0.08)'};
    }
`

const KeyLabel = styled.span`
    font-size: 0.6rem;
    color: rgba(255, 255, 255, 0.5);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`


/* --- NEW COMMUNICATION & UTILITY BARS --- */

const VisualBarContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    flex: 1; /* Take remaining width next to icon */

    &::before {
        content: '${({ $label }) => $label || ''}';
        font-size: 0.7rem;
        font-weight: 800;
        color: ${({ $color }) => $color || '#6366f1'};
        opacity: 0.9;
        letter-spacing: 2px;
        padding-left: 2px;
        text-transform: uppercase;
    }
`

const VisualBar = styled.div`
    display: flex;
    background: ${({ $bg }) => $bg || 'rgba(0, 0, 0, 0.2)'};
    border: 1px solid ${({ $color }) => $color ? `${$color}44` : 'rgba(255, 255, 255, 0.05)'};
    border-radius: 12px;
    overflow: hidden;
    padding: 24px 0; /* More vertical padding */
    transition: all 0.3s ease;
    flex-wrap: wrap; /* Prevent squishing on small widths */
    align-items: flex-end;
    
    &:hover {
        border-color: ${({ $color }) => $color ? `${$color}88` : 'rgba(255, 255, 255, 0.1)'};
        background: ${({ $bg, $color }) => $bg ? `${$bg.replace('0.2', '0.25')}` : ($color ? `${$color}11` : 'rgba(255, 255, 255, 0.03)')};
    }
`

const BarSlot = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    border-right: 1px solid rgba(255, 255, 255, 0.05);
    transition: all 0.2s;
    padding: 0px 10px;

    &:last-child {
        border-right: none;
    }
`

const WeaponIconWrapper = styled.div`
    position: relative;
    width: 100%;
    max-width: 50px;
    height: 25px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 2px;
    
    img {
        object-fit: contain;
        filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));
        opacity: 0.9;
    }

    svg {
        font-size: 20px;
        color: rgba(255, 255, 255, 0.9);
        filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));
    }
`


const Binds_HelpIconWrapper = styled.div`
    display: flex;
    align-items: center;
    color: rgba(99, 102, 241, 0.4);
    cursor: help;
    position: relative;
    transition: color 0.2s;

    &:hover {
        color: #6366f1;
        
        .tooltip {
            opacity: 1;
            visibility: visible;
            transform: translateY(-50%) translateX(0);
        }
    }

    svg {
        font-size: 14px !important;
    }
`

const Binds_Tooltip = styled.div`
    position: absolute;
    top: 50%;
    left: calc(100% + 10px);
    transform: translateY(-50%) translateX(-8px);
    background: #0d1117;
    color: #fff;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 10px;
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0.2px;
    width: 180px;
    opacity: 0;
    visibility: hidden;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    pointer-events: none;
    z-index: 100;
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
    text-align: left;
    line-height: 1.4;

    &::after {
        content: '';
        position: absolute;
        top: 50%;
        right: 100%;
        transform: translateY(-50%);
        border-width: 5px;
        border-style: solid;
        border-color: transparent rgba(255, 255, 255, 0.1) transparent transparent;
    }
`

const BindLabel = ({ label, desc }) => {
    return (
        <Binds_LabelText>
            {label}
            {desc && (
                <Binds_HelpIconWrapper>
                    <HelpOutlineIcon />
                    <Binds_Tooltip className="tooltip">{desc}</Binds_Tooltip>
                </Binds_HelpIconWrapper>
            )}
        </Binds_LabelText>
    )
}

const Binds_ActionButton = styled.div`
    background: ${({ $danger }) => ($danger ? 'rgba(239, 68, 68, 0.1)' : 'rgba(99, 102, 241, 0.1)')};
    border: 1px solid ${({ $danger }) => ($danger ? 'rgba(239, 68, 68, 0.3)' : 'rgba(255, 255, 255, 0.05)')};
    color: ${({ $danger }) => ($danger ? '#ef4444' : '#fff')};
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 0.70rem;
    font-weight: 600;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    white-space: nowrap;
    opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    &:hover {
        background: ${({ $danger }) => ($danger ? 'rgba(239, 68, 68, 0.2)' : 'rgba(99, 102, 241, 0.15)')};
        color: ${({ $danger }) => ($danger ? '#ff5555' : 'white')};
        border-color: ${({ $danger }) => ($danger ? '#ef4444' : '#6366f1')};
        transform: ${({ disabled }) => (disabled ? 'none' : 'translateY(-1px)')};
    }
`



const MovementGroup = styled.div`
display: flex;
gap: 20px;
align-items: flex-start;
width: 100%;
`

export default function BindsBasicModule({ id, initialExpanded }) {
    const { configData, updateConfig, activeModules, toggleModule, resetBasicBinds, clearBasicBinds, configIsLocked } = useConfig()
    const { t } = useLanguage()
    const { showConfirm } = useUI()

    const binds = configData.binds || {}
    const isActive = activeModules.includes('binds_basic')

    const handleKeyChange = (key, value) => {
        updateConfig('binds', key, value)
    }

    const handleRestoreDefaults = async (e) => {
        e.stopPropagation()
        if (configIsLocked) return
        const confirmed = await showConfirm({
            title: t('RESTORING_DEFAULTS'),
            message: t('RESTORING_DEFAULTS_CONFIRM'),
            confirmText: t('RESTORE'),
            variant: 'primary',
        })
        if (confirmed) resetBasicBinds()
    }

    const handleClearAll = async (e) => {
        e.stopPropagation()
        if (configIsLocked) return
        const confirmed = await showConfirm({
            title: t('CLEAR_ALL_BINDS'),
            message: t('CLEAR_ALL_BINDS_CONFIRM'),
            confirmText: t('CLEAR_ALL'),
            variant: 'danger',
        })
        if (confirmed) clearBasicBinds()
    }

    return (
        <ModuleContainer
            id={id}
            title={t('BINDS_BASIC_TITLE')}
            description={t('BINDS_BASIC_DESC')}
            icon={KeyboardIcon}
            moduleKey="binds_basic"
            isActive={isActive}
            onToggle={() => toggleModule('binds_basic')}
            minHeight="250px"
            initialExpanded={initialExpanded}
        >
            <Binds_Controls isActive={isActive}>
                {isActive && (
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginBottom: '4px' }}>
                        <Binds_ActionButton onClick={handleRestoreDefaults} disabled={configIsLocked} title={t('RESTORE_DEFAULTS')}>
                            <HistoryIcon style={{ fontSize: 14, marginRight: 4 }} />
                            {t('RESTORE')}
                        </Binds_ActionButton>
                        <Binds_ActionButton $danger onClick={handleClearAll} disabled={configIsLocked} title={t('CLEAR_ALL')}>
                            <DeleteSweepIcon style={{ fontSize: 14, marginRight: 4 }} />
                            {t('CLEAR')}
                        </Binds_ActionButton>
                    </div>
                )}
                {/* MOVEMENT */}
                <Binds_ModuleBody>
                        <SectionRow>
                            <SectionIconBinder $color="#3b82f6" $bg="rgba(59, 130, 246, 0.1)">
                                <AccessibilityIcon />
                            </SectionIconBinder>
                            <SectionContent>

                                <MovementGroup>
                                    <VisualBarContainer $label="MOVEMENT GRID" $color="#3b82f6" style={{ flex: '0 0 auto', width: 'auto', marginRight: '24px' }}>
                                        <KeyboardGridWrapper>
                                            {/* Top Row: Q W E R */}
                                            <KeyboardRow>
                                                <KeyBlock>
                                                    <KeyLabel>{t('LAST_WEAPON')}</KeyLabel>
                                                    <KeySelector value={binds.key_lastinv} onChange={(v) => handleKeyChange('key_lastinv', v)} fieldPath="binds.key_lastinv" disabled={!isActive || configIsLocked} style={{ width: '60px' }} />
                                                </KeyBlock>
                                                <KeyBlock $highlight>
                                                    <KeyLabel><KeyboardArrowUpIcon style={{ fontSize: '18px' }} /></KeyLabel>
                                                    <KeySelector value={binds.key_forward} onChange={(v) => handleKeyChange('key_forward', v)} fieldPath="binds.key_forward" disabled={!isActive || configIsLocked} style={{ width: '60px' }} />
                                                </KeyBlock>
                                                <KeyBlock>
                                                    <KeyLabel>{t('USE')}</KeyLabel>
                                                    <KeySelector value={binds.key_use} onChange={(v) => handleKeyChange('key_use', v)} fieldPath="binds.key_use" disabled={!isActive || configIsLocked} style={{ width: '60px' }} />
                                                </KeyBlock>
                                                <KeyBlock>
                                                    <KeyLabel>{t('RELOAD')}</KeyLabel>
                                                    <KeySelector value={binds.key_reload} onChange={(v) => handleKeyChange('key_reload', v)} fieldPath="binds.key_reload" disabled={!isActive || configIsLocked} style={{ width: '60px' }} />
                                                </KeyBlock>
                                            </KeyboardRow>

                                            {/* Bottom Row: A S D */}
                                            <KeyboardRow $offset="0px">
                                                <KeyBlock $highlight>
                                                    <KeyLabel><KeyboardArrowLeftIcon style={{ fontSize: '18px' }} /></KeyLabel>
                                                    <KeySelector value={binds.key_left} onChange={(v) => handleKeyChange('key_left', v)} fieldPath="binds.key_left" disabled={!isActive || configIsLocked} style={{ width: '60px' }} />
                                                </KeyBlock>
                                                <KeyBlock $highlight>
                                                    <KeyLabel><KeyboardArrowDownIcon style={{ fontSize: '18px' }} /></KeyLabel>
                                                    <KeySelector value={binds.key_back} onChange={(v) => handleKeyChange('key_back', v)} fieldPath="binds.key_back" disabled={!isActive || configIsLocked} style={{ width: '60px' }} />
                                                </KeyBlock>
                                                <KeyBlock $highlight>
                                                    <KeyLabel><KeyboardArrowRightIcon style={{ fontSize: '18px' }} /></KeyLabel>
                                                    <KeySelector value={binds.key_right} onChange={(v) => handleKeyChange('key_right', v)} fieldPath="binds.key_right" disabled={!isActive || configIsLocked} style={{ width: '60px' }} />
                                                </KeyBlock>
                                            </KeyboardRow>
                                        </KeyboardGridWrapper>
                                    </VisualBarContainer>

                                    <VisualBarContainer $label="ACTIONS" $color="#3b82f6" style={{ flex: '0 0 auto' }}>
                                        <KeyboardGridWrapper>
                                            <KeyboardRow>
                                                <KeyBlock>
                                                    <KeyLabel>{t('WALK')}</KeyLabel>
                                                    <KeySelector value={binds.key_walk} onChange={(v) => handleKeyChange('key_walk', v)} fieldPath="binds.key_walk" disabled={!isActive || configIsLocked} style={{ width: '60px' }} />
                                                </KeyBlock>
                                                <KeyBlock>
                                                    <KeyLabel>{t('DUCK')}</KeyLabel>
                                                    <KeySelector value={binds.key_duck} onChange={(v) => handleKeyChange('key_duck', v)} fieldPath="binds.key_duck" disabled={!isActive || configIsLocked} style={{ width: '60px' }} />
                                                </KeyBlock>
                                            </KeyboardRow>
                                            <KeyboardRow>
                                                <KeyBlock>
                                                    <KeyLabel>{t('JUMP')}</KeyLabel>
                                                    <KeySelector value={binds.key_jump} onChange={(v) => handleKeyChange('key_jump', v)} fieldPath="binds.key_jump" disabled={!isActive || configIsLocked} style={{ width: '60px' }} />
                                                </KeyBlock>
                                                <KeyBlock>
                                                    <KeyLabel>BUY</KeyLabel>
                                                    <KeySelector value={binds.key_buymenu} onChange={(v) => handleKeyChange('key_buymenu', v)} fieldPath="binds.key_buymenu" disabled={!isActive || configIsLocked} style={{ width: '60px' }} />
                                                </KeyBlock>
                                            </KeyboardRow>
                                        </KeyboardGridWrapper>
                                    </VisualBarContainer>
                                </MovementGroup>
                            </SectionContent>
                        </SectionRow>
                </Binds_ModuleBody>

                {/* COMBAT */}
                <Binds_ModuleBody>
                        <SectionRow>
                            <SectionIconBinder $color="#ef4444" $bg="rgba(239, 68, 68, 0.1)">
                                <SecurityIcon />
                            </SectionIconBinder>
                            <SectionContent>
                                <MovementGroup>
                                    <VisualBarContainer $label="COMBAT CONTROL" $color="#ef4444">
                                        <VisualBar $color="#ef4444" $bg="rgba(239, 68, 68, 0.05)">
                                            <BarSlot>
                                                <SlotLabel>ATTACK</SlotLabel>
                                                <KeySelector value={binds.key_attack} onChange={(v) => handleKeyChange('key_attack', v)} fieldPath="binds.key_attack" disabled={!isActive || configIsLocked} />
                                            </BarSlot>
                                            <BarSlot>
                                                <SlotLabel>AIM | ZOOM</SlotLabel>
                                                <KeySelector value={binds.key_attack2} onChange={(v) => handleKeyChange('key_attack2', v)} fieldPath="binds.key_attack2" disabled={!isActive || configIsLocked} />
                                            </BarSlot>
                                            <BarSlot>
                                                <SlotLabel>{t('RELOAD')}</SlotLabel>
                                                <KeySelector value={binds.key_reload} onChange={(v) => handleKeyChange('key_reload', v)} fieldPath="binds.key_reload" disabled={!isActive || configIsLocked} />
                                            </BarSlot>
                                        </VisualBar>
                                    </VisualBarContainer>

                                    <VisualBarContainer $label="TACTICAL ACTIONS" $color="#ef4444">
                                        <VisualBar $color="#ef4444" $bg="rgba(239, 68, 68, 0.05)">
                                            <BarSlot>
                                                <SlotLabel>{t('DROP_WEAPON')}</SlotLabel>
                                                <KeySelector value={binds.key_drop} onChange={(v) => handleKeyChange('key_drop', v)} fieldPath="binds.key_drop" disabled={!isActive || configIsLocked} />
                                            </BarSlot>
                                            <BarSlot>
                                                <SlotLabel>PING</SlotLabel>
                                                <KeySelector value={binds.key_ping} onChange={(v) => handleKeyChange('key_ping', v)} fieldPath="binds.key_ping" disabled={!isActive || configIsLocked} />
                                            </BarSlot>
                                            <BarSlot>
                                                <SlotLabel>{t('INSPECT')}</SlotLabel>
                                                <KeySelector value={binds.key_inspect} onChange={(v) => handleKeyChange('key_inspect', v)} fieldPath="binds.key_inspect" disabled={!isActive || configIsLocked} />
                                            </BarSlot>
                                        </VisualBar>
                                    </VisualBarContainer>
                                </MovementGroup>

                                <VisualBarContainer $label="WEAPON SLOTS & INVENTORY" $color="#ef4444">
                                    <VisualBar $color="#ef4444" $bg="rgba(239, 68, 68, 0.05)">
                                        <BarSlot>
                                            <WeaponIconWrapper>
                                                <Image src={getItemIcon('ak47')} alt="Primary" fill sizes="80px" />
                                            </WeaponIconWrapper>
                                            <SlotLabel>Primary</SlotLabel>
                                            <KeySelector value={binds.key_slot1} onChange={(v) => handleKeyChange('key_slot1', v)} fieldPath="binds.key_slot1" disabled={!isActive || configIsLocked} />
                                        </BarSlot>
                                        <BarSlot>
                                            <WeaponIconWrapper>
                                                <Image src={getItemIcon('deagle')} alt="Secondary" fill sizes="80px" />
                                            </WeaponIconWrapper>
                                            <SlotLabel>Secondary</SlotLabel>
                                            <KeySelector value={binds.key_slot2} onChange={(v) => handleKeyChange('key_slot2', v)} fieldPath="binds.key_slot2" disabled={!isActive || configIsLocked} />
                                        </BarSlot>
                                        <BarSlot>
                                            <WeaponIconWrapper>
                                                <Image src={getItemIcon('knife')} alt="Knife" fill sizes="80px" />
                                            </WeaponIconWrapper>
                                            <SlotLabel>Knife</SlotLabel>
                                            <KeySelector value={binds.key_slot3} onChange={(v) => handleKeyChange('key_slot3', v)} fieldPath="binds.key_slot3" disabled={!isActive || configIsLocked} />
                                        </BarSlot>
                                        <BarSlot>
                                            <WeaponIconWrapper>
                                                <Image src={getItemIcon('hegrenade')} alt="Grenades" fill sizes="80px" />
                                            </WeaponIconWrapper>
                                            <SlotLabel>Grenades</SlotLabel>
                                            <KeySelector value={binds.key_slot4} onChange={(v) => handleKeyChange('key_slot4', v)} fieldPath="binds.key_slot4" disabled={!isActive || configIsLocked} />
                                        </BarSlot>
                                        <BarSlot>
                                            <WeaponIconWrapper>
                                                <Image src={getItemIcon('c4')} alt="Bomb" fill sizes="80px" />
                                            </WeaponIconWrapper>
                                            <SlotLabel>Bomb</SlotLabel>
                                            <KeySelector value={binds.key_slot5} onChange={(v) => handleKeyChange('key_slot5', v)} fieldPath="binds.key_slot5" disabled={!isActive || configIsLocked} />
                                        </BarSlot>
                                        <BarSlot>
                                            <SlotLabel>NEXT</SlotLabel>
                                            <KeySelector value={binds.key_inv_next} onChange={(v) => handleKeyChange('key_inv_next', v)} fieldPath="binds.key_inv_next" disabled={!isActive || configIsLocked} />
                                        </BarSlot>
                                        <BarSlot>
                                            <SlotLabel>PREV</SlotLabel>
                                            <KeySelector value={binds.key_inv_prev} onChange={(v) => handleKeyChange('key_inv_prev', v)} fieldPath="binds.key_inv_prev" disabled={!isActive || configIsLocked} />
                                        </BarSlot>
                                    </VisualBar>
                                </VisualBarContainer>
                            </SectionContent>
                        </SectionRow>
                </Binds_ModuleBody>



                {/* COMMUNICATION */}
                <Binds_ModuleBody>
                        <SectionRow>
                            <SectionIconBinder $color="#10b981" $bg="rgba(16, 185, 129, 0.1)">
                                <ForumIcon />
                            </SectionIconBinder>
                            <SectionContent>
                                <VisualBarContainer $label="VOICE & CHAT" $color="#10b981">
                                    <VisualBar $color="#10b981" $bg="rgba(16, 185, 129, 0.05)">
                                        <BarSlot>
                                            <WeaponIconWrapper>
                                                <Image src={getItemIcon('microphone')} alt="Microphone" fill sizes="80px" />
                                            </WeaponIconWrapper>
                                            <SlotLabel>{t('MICROPHONE')}</SlotLabel>
                                            <KeySelector value={binds.key_mic} onChange={(v) => handleKeyChange('key_mic', v)} fieldPath="binds.key_mic" disabled={!isActive || configIsLocked} />
                                        </BarSlot>
                                        <BarSlot>
                                            <WeaponIconWrapper>
                                                <PublicIcon />
                                            </WeaponIconWrapper>
                                            <SlotLabel>{t('GLOBAL_CHAT')}</SlotLabel>
                                            <KeySelector value={binds.key_chat} onChange={(v) => handleKeyChange('key_chat', v)} fieldPath="binds.key_chat" disabled={!isActive || configIsLocked} />
                                        </BarSlot>
                                        <BarSlot>
                                            <WeaponIconWrapper>
                                                <GroupIcon />
                                            </WeaponIconWrapper>
                                            <SlotLabel>{t('TEAM_CHAT')}</SlotLabel>
                                            <KeySelector value={binds.key_chat_team} onChange={(v) => handleKeyChange('key_chat_team', v)} fieldPath="binds.key_chat_team" disabled={!isActive || configIsLocked} />
                                        </BarSlot>
                                    </VisualBar>
                                </VisualBarContainer>
                            </SectionContent>
                        </SectionRow>
                </Binds_ModuleBody>

                {/* UI & OTHERS */}
                <Binds_ModuleBody>
                        <SectionRow>
                            <SectionIconBinder $color="#06b6d4" $bg="rgba(6, 182, 212, 0.1)">
                                <AppSettingsAltIcon />
                            </SectionIconBinder>
                            <SectionContent>
                                <VisualBarContainer $label="INTERFACE & SYSTEM" $color="#06b6d4">
                                    <VisualBar $color="#06b6d4" $bg="rgba(6, 182, 212, 0.05)">
                                        <BarSlot>
                                            <SlotLabel>{t('SCOREBOARD')}</SlotLabel>
                                            <KeySelector value={binds.key_score} onChange={(v) => handleKeyChange('key_score', v)} fieldPath="binds.key_score" disabled={!isActive || configIsLocked} />
                                        </BarSlot>
                                        <BarSlot>
                                            <SlotLabel>{t('CONSOLE')}</SlotLabel>
                                            <KeySelector value={binds.key_console} onChange={(v) => handleKeyChange('key_console', v)} fieldPath="binds.key_console" disabled={!isActive || configIsLocked} />
                                        </BarSlot>
                                        <BarSlot>
                                            <SlotLabel>{t('KEY_TEAM_MENU')}</SlotLabel>
                                            <KeySelector value={binds.key_team_menu} onChange={(v) => handleKeyChange('key_team_menu', v)} fieldPath="binds.key_team_menu" disabled={!isActive || configIsLocked} />
                                        </BarSlot>
                                        <BarSlot>
                                            <SlotLabel>{t('KEY_SHOW_LOADOUT')}</SlotLabel>
                                            <KeySelector value={binds.key_show_loadout} onChange={(v) => handleKeyChange('key_show_loadout', v)} fieldPath="binds.key_show_loadout" disabled={!isActive || configIsLocked} />
                                        </BarSlot>
                                    </VisualBar>
                                </VisualBarContainer>

                                <VisualBarContainer $label="SYSTEM ACTIONS" $color="#06b6d4">
                                    <VisualBar $color="#06b6d4" $bg="rgba(6, 182, 212, 0.05)">
                                        <BarSlot>
                                            <SlotLabel>SPRAY</SlotLabel>
                                            <KeySelector value={binds.key_spray} onChange={(v) => handleKeyChange('key_spray', v)} fieldPath="binds.key_spray" disabled={!isActive || configIsLocked} />
                                        </BarSlot>
                                        <BarSlot>
                                            <SlotLabel>EQUIP ID</SlotLabel>
                                            <KeySelector value={binds.key_team_equip} onChange={(v) => handleKeyChange('key_team_equip', v)} fieldPath="binds.key_team_equip" disabled={!isActive || configIsLocked} />
                                        </BarSlot>
                                        <BarSlot>
                                            <SlotLabel>SCREENSHOT</SlotLabel>
                                            <KeySelector value={binds.key_screenshot} onChange={(v) => handleKeyChange('key_screenshot', v)} fieldPath="binds.key_screenshot" disabled={!isActive || configIsLocked} />
                                        </BarSlot>
                                        <BarSlot>
                                            <SlotLabel>QUIT</SlotLabel>
                                            <KeySelector value={binds.key_quit_prompt} onChange={(v) => handleKeyChange('key_quit_prompt', v)} fieldPath="binds.key_quit_prompt" disabled={!isActive || configIsLocked} />
                                        </BarSlot>
                                        <BarSlot>
                                            <SlotLabel>SELL ALL</SlotLabel>
                                            <KeySelector value={binds.key_sell_back_all} onChange={(v) => handleKeyChange('key_sell_back_all', v)} fieldPath="binds.key_sell_back_all" disabled={!isActive || configIsLocked} />
                                        </BarSlot>
                                        <BarSlot>
                                            <SlotLabel>CANCEL</SlotLabel>
                                            <KeySelector value={binds.key_cancel_select} onChange={(v) => handleKeyChange('key_cancel_select', v)} fieldPath="binds.key_cancel_select" disabled={!isActive || configIsLocked} />
                                        </BarSlot>
                                    </VisualBar>
                                </VisualBarContainer>
                            </SectionContent>
                        </SectionRow>
                </Binds_ModuleBody>
            </Binds_Controls>
        </ModuleContainer>
    )
}
